import type { RootState } from '../plugins/store';
import { BASE_COLOR } from './constants';
import type { IGroupTheme } from '../types/user';
import { getComplementaryColor } from '../utils/helpers';

/**
 * Màu base đang áp dụng, theo thứ tự ưu tiên:
 *   1. Override thủ công (theme slice) — dùng cho test / theme switcher.
 *   2. labels.backgroundColor từ group (dùng cho button, menu active).
 *   3. primaryColor từ group.
 *   4. primaryColor cũ ở attributes (backward compat).
 *   5. BASE_COLOR mặc định (#6A01FE).
 */
export const selectPrimaryColor = (state: RootState): string =>
    state.theme.override ||
    state.user.attributes?.groups?.[0]?.theme_color?.theme?.labels
        ?.backgroundColor ||
    state.user.attributes?.groups?.[0]?.theme_color?.theme?.primaryColor ||
    state.user.attributes?.primaryColor ||
    BASE_COLOR;

/** Logo áp dụng: group logo_image > attributes.logo > null (fallback SVG mặc định). */
export const selectUserLogo = (state: RootState): string | null =>
    state.user.attributes?.groups?.[0]?.logo_image ||
    state.user.attributes?.logo ||
    null;

/** theme_color của group đầu tiên mà user thuộc về, hoặc null nếu chưa có group. */
const selectGroupTheme = (state: RootState): IGroupTheme | null =>
    state.user.attributes?.groups?.[0]?.theme_color ?? null;

/** Màu text trên nền active (labels.textColor). Fallback = white. */
export const selectLabelTextColor = (state: RootState): string =>
    state.user.attributes?.groups?.[0]?.theme_color?.theme?.labels?.textColor ||
    '#FFFFFF';

/** Ảnh nền survey (survey_image). Fallback = null. */
export const selectSurveyImage = (state: RootState): string | null =>
    state.user.attributes?.groups?.[0]?.survey_image ?? null;

/** Màu chữ welcome text trên màn start/end survey. Fallback = white. */
export const selectWelcomeTextColor = (state: RootState): string =>
    state.user.attributes?.groups?.[0]?.theme_color?.survey?.welcome
        ?.textColor || '#FFFFFF';

/** Màu nền + chữ 5 card tổng quan dashboard. Fallback = màu gốc hardcode. */
export const selectDashboardCardColors = (state: RootState) => {
    const g = selectGroupTheme(state);
    const d = g?.pages?.dashboard;
    return {
        dataCollection: {
            bg: d?.dataCollectionCard?.backgroundColor || '#D4C1FF66',
            text: d?.dataCollectionCard?.textColor || 'inherit',
        },
        campaign: {
            bg: d?.campaignCard?.backgroundColor || '#FFEEE2',
            text: d?.campaignCard?.textColor || 'inherit',
        },
        servicePackage: {
            bg: d?.servicePackageCard?.backgroundColor || '#EFFBFF',
            text: d?.servicePackageCard?.textColor || 'inherit',
        },
        surveyService: {
            bg: d?.surveyServiceCard?.backgroundColor || '#FFEAED',
            text: d?.surveyServiceCard?.textColor || 'inherit',
        },
        account: {
            bg: d?.overviewCard?.backgroundColor || '#DDFFEB',
            text: d?.overviewCard?.textColor || 'inherit',
        },
    };
};

/** Màu nền + chữ 4 card tổng quan chiến dịch (surveyOverview). Fallback = màu gốc. */
export const selectSurveyOverviewCardColors = (state: RootState) => {
    const g = selectGroupTheme(state);
    const s = g?.pages?.surveyOverview;
    return {
        totalCollectedData: {
            bg:
                s?.totalCollectedDataCard?.backgroundColor ||
                'var(--color-primary-light-40)',
            text: s?.totalCollectedDataCard?.textColor || 'inherit',
            accentText: s?.totalCollectedDataCard?.textColor || BASE_COLOR,
        },
        totalParticipants: {
            bg: s?.totalParticipantsCard?.backgroundColor || '#FFEEE2',
            text: s?.totalParticipantsCard?.textColor || 'inherit',
            accentText: s?.totalParticipantsCard?.textColor || '#FF7916',
        },
        totalFreeCollectedData: {
            bg: s?.totalFreeCollectedDataCard?.backgroundColor || '#EFFBFF',
            text: s?.totalFreeCollectedDataCard?.textColor || 'inherit',
            accentText: s?.totalFreeCollectedDataCard?.textColor || '#475EFB',
        },
        totalPaidCollectedData: {
            bg: s?.totalPaidCollectedDataCard?.backgroundColor || '#FFEAED',
            text: s?.totalPaidCollectedDataCard?.textColor || 'inherit',
            accentText: s?.totalPaidCollectedDataCard?.textColor || '#FF0000',
        },
    };
};

/** [freeDataColor, serviceDataColor] — chart Dữ liệu thu thập. */
export const selectChartDataCollectionColors = (
    state: RootState
): [string, string] => {
    const g = selectGroupTheme(state);
    const primary = selectPrimaryColor(state);
    return [
        g?.charts?.dataCollection?.freeDataColor || primary,
        g?.charts?.dataCollection?.serviceDataColor || '#FF7916',
    ];
};

/** [campaignColor, dataCollectionColor] — chart Chiến dịch. */
export const selectChartCampaignColors = (
    state: RootState
): [string, string] => {
    const g = selectGroupTheme(state);
    const primary = selectPrimaryColor(state);
    return [
        g?.charts?.campaign?.campaignColor || '#FF7916',
        g?.charts?.campaign?.dataCollectionColor || primary,
    ];
};

/** [ios, android, web, other, windows] — chart Thiết bị (5 cột). Fallback = màu gốc. */
export const selectChartDeviceColors = (state: RootState): string[] => {
    const g = selectGroupTheme(state);
    return [
        g?.charts?.device?.iosColor || '#5C6BC0',
        g?.charts?.device?.androidColor || '#26A69A',
        g?.charts?.device?.webColor || '#EC407A',
        g?.charts?.device?.otherColor || '#FFA726',
        g?.charts?.device?.windownColor || '#42A5F5',
    ];
};

/** [HaNoi, HCM, DaNang, Other, primary] — chart Khu vực (pie). */
export const selectChartRegionColors = (state: RootState): string[] => {
    const g = selectGroupTheme(state);
    const primary = selectPrimaryColor(state);
    return [
        g?.charts?.region?.HaNoiColor || '#FF7916',
        g?.charts?.region?.HCMColor || '#FFE0B1',
        g?.charts?.region?.DaNangColor || '#CED5FF',
        g?.charts?.region?.OtherColor || '#CFAFFF',
        primary,
    ];
};

/** [SystemAdminColor, ClientColor] — chart Tài khoản. */
export const selectChartAccountColors = (
    state: RootState
): [string, string] => {
    const g = selectGroupTheme(state);
    const primary = selectPrimaryColor(state);
    return [
        g?.charts?.account?.SystemAdminColor || primary,
        g?.charts?.account?.ClientColor || '#FF7916',
    ];
};

/** Màu 4 biểu đồ tròn user profile trong tab tổng kết.
 *  Fallback: không có config → [BASE_COLOR, '#FF7916']; có color1 → color2 = complementary của color1. */
export const selectChartSummaryProfileColors = (state: RootState) => {
    const g = selectGroupTheme(state);
    const buildPair = (c1?: string, c2?: string): [string, string] => {
        const color1 = c1 || BASE_COLOR;
        const color2 = c2 || (c1 ? getComplementaryColor(c1) : '#FF7916');
        return [color1, color2];
    };
    return {
        userProfile: buildPair(
            g?.charts?.userProfile?.color1,
            g?.charts?.userProfile?.color2
        ),
        gender: buildPair(g?.charts?.gender?.color1, g?.charts?.gender?.color2),
        marriage: buildPair(
            g?.charts?.marriage?.color1,
            g?.charts?.marriage?.color2
        ),
        children: buildPair(
            g?.charts?.children?.color1,
            g?.charts?.children?.color2
        ),
    };
};
